interface CrossAppCacheEntry {
  value: unknown;
  ttl?: number;
  timestamp: number;
  cacheOnlyOnce?: boolean;
  isAsync?: boolean;
}

declare global {
  interface Window {
    __hydra_cross_app_cache__?: {
      cache: Record<string, CrossAppCacheEntry>;
      setValue: ({
        key,
        value,
        ttl,
        cacheOnlyOnce,
        isAsync,
      }: {
        key: string;
        value: unknown;
        ttl?: number;
        cacheOnlyOnce?: boolean;
        isAsync?: boolean;
      }) => void;
      getValue: (key: string) => unknown;
      getValueAsync: (key: string) => Promise<unknown>;
    };
  }
}

/** Simple cross-app cache for storing request data in the browser's global state.
 * Intended to prevent duplicate requests to the same endpoint in different apps,
 * particularly during the initial load of the app.
 *
 * Could probably be a separate package in the future, but it's simple enough
 * to keep it here for now.
 */
export function initializeCrossAppCache() {
  if (window.__hydra_cross_app_cache__ != null) {
    return window.__hydra_cross_app_cache__;
  }

  window.__hydra_cross_app_cache__ = {
    cache: {},
    setValue: ({
      key,
      value,
      ttl,
      cacheOnlyOnce,
      isAsync,
    }: {
      key: string;
      value: unknown;
      ttl?: number;
      cacheOnlyOnce?: boolean;
      isAsync?: boolean;
    }) => {
      setCrossAppCache({ key, value, ttl, cacheOnlyOnce, isAsync });
    },
    getValue: (key: string) => {
      return getCrossAppCache(key);
    },
    getValueAsync: (key: string) => {
      return getCrossAppCacheAsync(key);
    },
  };

  return window.__hydra_cross_app_cache__;
}

export const crossAppCache = initializeCrossAppCache();

/** Caches a value for use within Hydra or in other app bundles.
 *
 * @param key - The key to cache the value under.
 * @param value - The value to cache.
 * @param ttl - The time to live for the cached value, in milliseconds.
 * @param cacheOnlyOnce - If true, the value will only be cached once, and never overwritten.
 * To be used in conjunction with `ttl` for caching requests during the initial load of the app, where
 * the value is expected to be the same for a short period of time, but might be updated later.
 */
export function setCrossAppCache({
  key,
  value,
  ttl,
  cacheOnlyOnce = false,
  isAsync = false,
}: {
  key: string;
  value: unknown;
  ttl?: number;
  cacheOnlyOnce?: boolean;
  isAsync?: boolean;
}) {
  if (window.__hydra_cross_app_cache__ == null) {
    return;
  }

  if (cacheOnlyOnce === true && window.__hydra_cross_app_cache__.cache[key] != null) {
    return;
  }

  window.__hydra_cross_app_cache__.cache[key] = {
    value,
    ttl,
    timestamp: Date.now(),
    cacheOnlyOnce,
    isAsync,
  };
}

function internalGetCrossAppCache(key: string, { allowAsync = false }: { allowAsync?: boolean } = {}) {
  if (window.__hydra_cross_app_cache__ == null) {
    return null;
  }
  const cachedValue = window.__hydra_cross_app_cache__.cache[key];
  if (cachedValue == null) {
    return null;
  }
  if (cachedValue.ttl != null && cachedValue.timestamp + cachedValue.ttl < Date.now()) {
    return null;
  }

  if (cachedValue.isAsync === true && !allowAsync) {
    return null;
  }

  return cachedValue.value;
}

export function getCrossAppCache(key: string) {
  return internalGetCrossAppCache(key, { allowAsync: false });
}

export async function getCrossAppCacheAsync(key: string) {
  return await internalGetCrossAppCache(key, { allowAsync: true });
}
